import {
  IExcelNotification,
  ILettersCertificates,
  PayrollReceipt,
  SendApproval,
  SendCode,
  SendReport
} from './types'
import { getData } from '../get-table'
import { HttpService } from '../http.service'
export * from './types'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)

const http = new HttpService()

/* convertir array a string */
export async function convertArrayToString(array: any) {
  return array.length === 0 ? '' : "'" + array.join("','") + "'"
}

export async function getProcessCountry(): Promise<any> {
  const response = await getData<any>({
    tables: ['pg_processes'],
    fields: ['processes.code as id', 'processes.name'],
    join: [
      'INNER JOIN LATERAL jsonb_array_elements_text(processes.countries) c(country) ON TRUE',
      'INNER JOIN countries ON countries.id = c.country::uuid'
    ],
    where: ['countries.id in (:countryId)']
  })

  return response[0].results
}

export async function getCurrencies() {
  const fields = []
  const where = ['is_deleted = false']

  fields.push('id', "CONCAT(code,' - ',name) as name")
  const data = await getData<any>({
    tables: ['pg_currencies'],
    fields,
    where: where,
    modifier: ['order by code asc']
  })

  return data[0].results
}

export async function getClosedPayrolls(
  payrollId: string[],
  processesCode: string,
  startDate: string,
  endDate: string
) {
  const where = [
    `processed_payrolls.payroll_id IN ('${payrollId.map(p => p).join("','")}')`,
    `processed_payrolls.start_date>='${startDate}' AND processed_payrolls.end_date<='${endDate}'`,
    `payroll_status.code='CLOSED'`,
    `processes.code='${processesCode}'`
  ]
  const data = await getData<any>({
    tables: ['pg_processed_payrolls'],
    fields: [
      'processed_payrolls.id',
      'processed_payrolls.payroll_id',
      'processes.code as process',
      'processed_payrolls.start_date',
      'processed_payrolls.end_date',
      'processed_payrolls.employees',
      "CONCAT(payrolls.code,': ',TO_CHAR(processed_payrolls.start_date,'dd-mm-yyyy'),' - ',TO_CHAR(processed_payrolls.end_date,'dd-mm-yyyy')) as name"
    ],
    join: [
      'INNER JOIN payroll_status ON processed_payrolls.payroll_status_id = payroll_status.id',
      'INNER JOIN processes ON processed_payrolls.process_id = processes.id',
      'INNER JOIN payrolls ON processed_payrolls.payroll_id = payrolls.id'
    ],
    where,
    modifier: ['order by payrolls.code ASC, processed_payrolls.end_date DESC']
  })

  return data[0].results
}

export async function getEmployees(
  payrollId: string,
  conditions: string,
  employeesIds: string = ''
) {
  const where = ['employees.is_deleted=false']
  if (payrollId !== '') where.push(`employees.payroll_id in ('${payrollId}')`)
  if (employeesIds !== '') where.push(`employees.id in (${employeesIds})`)
  if (conditions !== '') where.push(conditions)

  const data = await getData<any>({
    tables: ['pg_employees'],
    fields: [
      'distinct employees.id',
      'employees.name',
      'directions.name as direccion',
      'managements.name as gerencia',
      'departments.name as departamento',
      'sucursals.name as sucursal',
      'positions.name as cargo',
      'turns.name as turno',
      'employees.leave_date as fechaSalida',
      "CONCAT(payrolls.code,': ',employees.code) as codigo",
      'joining_reasons.name as causaIngreso',
      "CONCAT(payrolls.code,': ',employees.code,' - ',employees.name) as codigo_name",
      'false as isChecked',
      "CONCAT(employer_records.code,' - ',custom_options.name) as registroPatronal"
    ],
    join: [
      'LEFT JOIN directions ON employees.direction_id = directions.id',
      'LEFT JOIN managements ON employees.management_id = managements.id',
      'LEFT JOIN departments ON employees.department_id = departments.id',
      'LEFT JOIN sucursals ON employees.sucursal_id = sucursals.id',
      'LEFT JOIN positions ON employees.position_id = positions.id',
      'LEFT JOIN turns ON employees.turn_id = turns.id',
      'LEFT JOIN joining_reasons ON employees.joining_reason_id = joining_reasons.id',
      'INNER JOIN payrolls ON employees.payroll_id = payrolls.id',
      'LEFT JOIN employer_records ON employees.employer_record_id = employer_records.id',
      "LEFT JOIN custom_options ON json_extract_path_text(employer_records.custom_fields::json, 'custom:mxRiskClass')::uuid = custom_options.id"
    ],
    where,
    modifier: ['order by employees.name asc']
  })

  return data[0].results
}

export async function getPayrollCurrency(payrollId: string) {
  const fields = []
  const where = [`id='${payrollId}'`, 'is_deleted = false']

  fields.push('currency_id as currencyFromId')
  const data = await getData<any>({
    tables: ['pg_payrolls'],
    fields,
    where: where
  })

  return data[0].results[0].currencyFromId
}

export async function getExchangeRatePayroll(payrollId: string) {
  const fields = []
  const where = [`payroll_id='${payrollId}'`, 'is_deleted = false']

  fields.push(
    'currency_from_id as currencyFromId',
    'currency_to_id as currencyToId',
    'exchange_rate as exchangeRatePayroll',
    'start_date as startDate',
    'end_date as endDate'
  )
  const data = await getData<any>({
    tables: ['pg_exchange_rates'],
    fields,
    where: where
  })

  return data[0].results
}

export async function getTemplates(
  processCode: string,
  payrolls: string[]
): Promise<any> {
  const where = [
    `receipt_templates.country_id=:countryId`,
    `(receipt_templates.code ILIKE 'payment%')`,
    `(jsonb_array_length(receipt_templates.companies) = 0 OR c.company::uuid =:companyId)`
  ]
  if (processCode) {
    where.push(`(processes.code='${processCode}' OR processes.code IS NULL)`)
  }
  if (payrolls.length > 0) {
    where.push(
      `(jsonb_array_length(receipt_templates.payrolls) = 0 OR p.payroll::uuid = ANY(ARRAY[${payrolls
        .map(p => `'${p}'`)
        .join(', ')}]::uuid[]))`
    )
  }
  const response: any = await getData({
    tables: ['pg_receipt_templates'],
    fields: [
      'receipt_templates.id',
      'receipt_templates.name',
      'receipt_templates.is_standard as isStandard',
      'processes.code',
      'payrolls.id as payrollId',
      'companies.id as companyId',
      'receipt_templates.country_id as countryId'
    ],
    where,
    join: [
      'LEFT JOIN LATERAL jsonb_array_elements_text(receipt_templates.companies) c(company) ON TRUE',
      'LEFT JOIN companies ON companies.id = c.company::uuid',
      'LEFT JOIN LATERAL jsonb_array_elements_text(receipt_templates.payrolls) p(payroll) ON TRUE',
      'LEFT JOIN payrolls ON payrolls.id = p.payroll::uuid',
      'LEFT JOIN LATERAL jsonb_array_elements_text(receipt_templates.process) pr(process) ON TRUE',
      'LEFT JOIN processes ON processes.id = pr.process::uuid'
    ],
    modifier: [
      'order by receipt_templates.created_at desc,receipt_templates.is_standard asc'
    ],
    options: {
      outputFormat: 'json'
    }
  })
  return response[0].results
}

export async function generatePayrollReceipt(
  data: PayrollReceipt
): Promise<string> {
  const response: any = await http.post('reports/', {
    code: data.code,
    parameters: {
      templateId: data.parameters.templateId,
      processedPayrollId: data.parameters.processedPayrollId,
      paymentCurrency: {
        coinId: data.parameters.paymentCurrency.coinId,
        exchangeRate: Number(data.parameters.paymentCurrency.exchangeRate)
      },
      additionalTextA: data.parameters.additionalTextA,
      additionalTextB: data.parameters.additionalTextB,
      additionalTextC: data.parameters.additionalTextC,
      employees: data.parameters.employees
    }
  })

  if (response?.data?.data?.error) {
    return response?.data?.data?.error
  }
  return response?.data
}

export async function sendEmailsPayrollReceipts(
  data: PayrollReceipt
): Promise<string> {
  const response: any = await http.post('email-private/send-emails', {
    code: data.code,
    module: data.module,
    subject: data.subject,
    parameters: {
      templateId: data.parameters.templateId,
      processedPayrollId: data.parameters.processedPayrollId,
      paymentCurrency: {
        coinId: data.parameters.paymentCurrency.coinId,
        exchangeRate: data.parameters.paymentCurrency.exchangeRate
      },
      additionalTextA: data.parameters.additionalTextA,
      additionalTextB: data.parameters.additionalTextB,
      additionalTextC: data.parameters.additionalTextC,
      employees: data.parameters.employees
    }
  })

  if (response?.data?.data?.error) {
    return response?.data?.data?.error
  }
  return response?.data
}

export async function getRequest(id: string) {
  const data = await getData<any>({
    tables: ['pg_reports_requests'],
    fields: [
      'reports_requests.errors',
      'process_status.name as status',
      'reports_requests.url'
    ],
    join: [
      'INNER JOIN process_status ON reports_requests.status_id=process_status.id'
    ],
    where: [`reports_requests.id ='${id}'`]
  })

  return data[0].results[0]
}

export async function getPayroll(payrollId: string) {
  const fields = []
  const where = [`id='${payrollId}'`]

  fields.push('send_receipt_email')
  const data = await getData<any>({
    tables: ['pg_payrolls'],
    fields,
    where: where
  })

  return data[0].results[0]?.send_receipt_email
}

export async function getPayrolls(userId: string) {
  const payrolls = await getData<any>({
    tables: ['pg_user_enabled_payrolls'],
    join: [
      'INNER JOIN payrolls ON user_enabled_payrolls.payroll_id = payrolls.id'
    ],
    fields: ['payrolls.code'],
    where: [`user_enabled_payrolls.user_id = '${userId}'`]
  })
  const data = await getData<any>({
    tables: ['pg_payrolls'],
    fields: [
      'payrolls.id',
      "CONCAT(payrolls.code,'-',payrolls.name) as name",
      'payrolls.name as payroll',
      'payrolls.code',
      'currencies.name as currency',
      'countries.id as countryId',
      'frequencies.name as frequency',
      'companies.name as company',
      'companies.id as companyId'
    ],
    join: [
      'INNER JOIN currencies ON payrolls.currency_id = currencies.id',
      'INNER JOIN companies ON payrolls.company_id = companies.id',
      'INNER JOIN countries ON companies.country_id = countries.id',
      'INNER JOIN frequencies ON frequencies.id = payrolls.frequency_id'
    ],
    where: [
      `payrolls.code IN (${payrolls[0].results
        .map((payroll: any) => `'${payroll.code}'`)
        .join(', ')})`,
      'companies.id = :companyId',
      'payrolls.is_inactive = false'
    ]
  })
  return data[0].results
}

export async function getListEmailsRequest(
  processedPayrollId: string[],
  offset: number,
  perPage: number
) {
  const fields = []
  const where = [
    `processed_payroll_id IN (${processedPayrollId
      .map(id => `'${id}'`)
      .join(', ')})`
  ]

  fields.push('id')
  const response = await getData<any>({
    tables: ['pg_emails_requests'],
    fields,
    where: where
  })

  const emailsRequestsProcessedPayrollId = response[0].results.map(
    (_: any) => _.id
  )
  let data = { results: [], totalItems: 0 }
  if (emailsRequestsProcessedPayrollId.length === 0) {
    return data
  } else {
    const strIds = await convertArrayToString(emailsRequestsProcessedPayrollId)
    return await getListNotifications(strIds, offset, perPage)
  }
}

export async function getListNotifications(
  idEmailsRequests: string,
  offset: number,
  perPage: number
) {
  const where = [`request_id in (${idEmailsRequests})`]

  const data = await getData<any>({
    tables: ['pg_emails_notifications'],
    fields: [
      "CONCAT(employees.code,' - ',employees.name) as employee",

      "TO_CHAR(emails_notifications.last_sent,'dd-mm-yyyy') as shippingDate",
      "TO_CHAR(emails_notifications.last_opened_in,'dd-mm-yyyy') as lastOpeningDate",
      "TO_CHAR(emails_notifications.approval_date,'dd-mm-yyyy') as approvalDate",
      'emails_notifications.errors'
    ],
    join: [
      'INNER JOIN employees ON  emails_notifications.employee_id= employees.id'
    ],
    where: where,
    options: {
      outputFormat: 'json',
      pagination: { offset, limit: perPage || 10 }
    }
  })

  return data[0]
}

export async function sendCode(data: SendCode): Promise<string> {
  try {
    const response: any = await http.post('email-public/send-code', {
      code: data.code,
      data: data.data
    })
    return response?.data
  } catch (error: any) {
    return error?.response?.data?.error || 'Error al enviar el código'
  }
}

export async function sendApprovalPayment(data: SendApproval): Promise<any> {
  try {
    const response: any = await http.post(
      'email-public/send-approval-payment',
      {
        data: data.data
      }
    )
    return response?.data
  } catch (error: any) {
    return error?.response?.data?.error || 'Error al enviar la aprobación'
  }
}

export async function sendReport(data: SendReport): Promise<any> {
  try {
    const response: any = await http.post('email-public/send-report', {
      data: data.data,
      autocode: data.autocode
    })
    return response?.data
  } catch (error: any) {
    return error?.response?.data?.error || 'Error al enviar el reporte'
  }
}

export async function generateExcelNotification(
  data: IExcelNotification
): Promise<string> {
  const response: any = await http.post('reports/', {
    code: data.code,
    parameters: {
      processedPayrollId: data.parameters.processedPayrollId
    }
  })

  if (response?.data?.data?.error) {
    return response?.data?.data?.error
  }
  return response?.data
}

export async function getEmployeesLettersCertificates(
  terminatedEmployees: boolean
) {
  const where = [
    'employees.payroll_id in (:payrollId)',
    'employees.is_deleted=false'
  ]
  if (!terminatedEmployees) where.push('employees.leave_date is null')

  const data = await getData<any>({
    tables: ['pg_employees'],
    fields: [
      'distinct employees.id',
      "CONCAT(employees.code,' - ',employees.name) as name"
    ],
    where,
    modifier: ['order by name asc']
  })

  return data[0].results
}

export async function getLettersCertificates() {
  const where = [
    'companies.id IN (:companyId)',
    'certificates_and_letters.is_deleted=false'
  ]

  const data = await getData<any>({
    tables: ['pg_certificates_and_letters'],
    fields: [
      'distinct certificates_and_letters.id',
      'certificates_and_letters.name'
    ],
    join: [
      'INNER JOIN LATERAL jsonb_array_elements_text(certificates_and_letters.companies) c(company) ON TRUE',
      `INNER JOIN companies ON companies.id = c.company::uuid`
    ],
    where,
    modifier: ['order by certificates_and_letters.name asc']
  })

  return data[0].results
}

export async function generateCertificates(
  data: ILettersCertificates
): Promise<string> {
  const response: any = await http.post('reports/', {
    code: data.code,
    parameters: {
      templateId: data.parameters.templateId,
      employee: data.parameters.employee
    }
  })

  if (response?.data?.data?.error) {
    return response?.data?.data?.error
  }
  return response?.data
}

export function validateExchangeRate(
  exchangeRatesPayroll: any,
  from: string,
  to: string,
  startDate: string,
  endDate: string
) {
  if (from === to) {
    return 1
  }

  const rate = exchangeRatesPayroll?.find(
    (rate: any) =>
      rate.currencyFromId === from &&
      rate.currencyToId === to &&
      dayjs.utc(rate.startDate).format('YYYY-MM-DD') <= startDate &&
      dayjs.utc(rate.endDate).format('YYYY-MM-DD') >= endDate
  )

  if (rate) {
    return Number(rate.exchangeRatePayroll)
  }
  const reverseRate = exchangeRatesPayroll?.find(
    (rate: any) =>
      rate.currencyFromId === to &&
      rate.currencyToId === from &&
      dayjs.utc(rate.startDate).format('YYYY-MM-DD') <= startDate &&
      dayjs.utc(rate.endDate).format('YYYY-MM-DD') >= endDate
  )

  if (reverseRate) {
    return 1 / Number(reverseRate.exchangeRatePayroll)
  }

  return 1
}

export function reduceEmployees(employees: Array<[]>) {
  let uniqueEmployees: any = []
  employees.reduce((res: any, value: any) => {
    if (!res[value]) {
      res[value] = value
      uniqueEmployees.push(value)
    }
    return res
  }, {})

  return uniqueEmployees
}
